import { PaymentsProvider } from '@zorro/clients';
import { Button, mapLegacyVariant } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Link } from '@zorro/design-system/v2/Link';
import { List } from '@zorro/design-system/v2/List';
import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { Select } from '@zorro/design-system/v2/Select';
import { Title } from '@zorro/design-system/v2/Title';
import { MODAL_ZINDEX } from '@zorro/types';
import {
  Group,
  Icon,
  Stack,
  Text,
  brand,
  useDisclosure,
} from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ElementType, useRef, useState } from 'react';
import { $enum } from 'ts-enum-util';

import { useAnalytics } from '../../Analytics';
import { FormFooter } from '../../FormFooter';
import { PaymentDetails } from '../../Payments/PaymentDetails/PaymentDetails';
import { PaymentMethodViewer } from '../../Payments/PaymentDetails/paymentMethodDisplayRules';
import { EmployeePaymentMethodDetails } from '../../Payments/PaymentMethod/EmployeePaymentMethodDetails';
import { PaymentAmountDistribution } from '../../Payments/PaymentMethod/PaymentAmountDistribution';
import { PaymentMethodType } from '../../Payments/PaymentMethod/PaymentMethodType';
import { getMonthlyAutoPayDateOptions } from '../../Payments/PaymentStatus/PaymentStatusUtils';
import { useLynxPaymentsContext } from '../../Payments/ZorroPay/hooks/useLynxPaymentsContext';
import {
  BUTTON_LABELS,
  CommunicationMessageConfig,
  FlagType,
} from './CommunicationConfig';
import { FilesPageDocumentsNote } from './FilesPageDocumentsNote';

type CommunicationMessageProps = CommunicationMessageConfig & {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  shouldShowZorroPayButton?: boolean;
  shouldShowPaymentMethodType?: boolean;
  onOpenSelfEnrollmentDrawer?: () => void;
  isEmployeeMode: boolean;
};

type ModalConfig = {
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
};

type ModalContentConfig = {
  title: string;
  message: string;
};

const AUTO_PAY_DRAWER_CONFIG = {
  title: 'Confirm auto pay',
  message:
    "Select your monthly payment date and confirm you've completed auto pay setup.",
} as const;

const getInitialPremiumPayment = (
  employeeName?: string
): ModalContentConfig => ({
  title: employeeName
    ? `${employeeName} must make their first premium payment`
    : 'Confirm initial premium payment',
  message: employeeName
    ? `${employeeName} needs to make their first premium payment in the carrier portal, using their Zorro Pay details.`
    : 'Are you sure you have completed your initial premium payment? Confirm to update your status',
});

const getAorModalConfig = (employeeName?: string): ModalContentConfig => ({
  title: employeeName
    ? 'Mark cancellation as done?'
    : 'Confirm cancellation of current plan',
  message: employeeName
    ? `Only do this if ${employeeName} has canceled their current plan with their carrier or the exchange. This removes the action from their account and can't be undone here.`
    : 'I have successfully canceled my current health plan with the carrier/exchange',
});

const getCarrierRequirementsModalConfig = (
  employeeName?: string
): ModalContentConfig => ({
  title: employeeName
    ? 'Mark carrier requirement as done?'
    : 'Confirm completion of carrier requirement',
  message: employeeName
    ? `Only do this if ${employeeName} has completed the carrier's requirement. This removes the action from their account and can't be undone here.`
    : 'I have successfully completed the specific requirement requested by the carrier',
});

const getModalConfig = (flagType: FlagType, employeeName?: string) => {
  return $enum.mapValue(flagType).with<ModalContentConfig | null>({
    [FlagType.INITIAL_PAYMENT]: getInitialPremiumPayment(employeeName),
    [FlagType.AOR]: getAorModalConfig(employeeName),
    [FlagType.CARRIER_APP_SENT]:
      getCarrierRequirementsModalConfig(employeeName),
    [FlagType.AUTO_PAY]: null,
    [FlagType.AUTO_PAY_ENROLLMENT_CONFIRMED]: null,
    [FlagType.SELF_ENROLL]: null,
    [FlagType.NEXT_STEPS]: null,
    [FlagType.RENEWAL]: null,
    [FlagType.MEDICARE]: null,
    [FlagType.MEDICARE_IFP]: null,
    [FlagType.GENERAL]: null,
  });
};

const AUTO_PAY_FLAG_TYPES = new Set([
  FlagType.AUTO_PAY,
  FlagType.AUTO_PAY_ENROLLMENT_CONFIRMED,
]);

const MONTHLY_AUTO_PAY_DATE_DATA = getMonthlyAutoPayDateOptions();

type ZorroPayDrawerContentProps = {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  shouldShowPaymentMethodType?: boolean;
};

const ZorroPayDrawerContent = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  shouldShowPaymentMethodType,
}: ZorroPayDrawerContentProps) => {
  const { paymentsProvider } = useLynxPaymentsContext({
    employerId,
    employeeId,
  });

  if (!paymentsProvider) {
    return null;
  }

  return $enum.mapValue(paymentsProvider).with({
    [PaymentsProvider.LYNX]: (
      <PaymentDetails
        viewer={PaymentMethodViewer.EMPLOYEE}
        employeeId={employeeId}
        employerId={employerId}
        onboardingPeriodId={onboardingPeriodId}
      />
    ),
    [PaymentsProvider.ECHO]: (
      <Stack gap="sm">
        <Stack mt="sm">
          {shouldShowPaymentMethodType && (
            <>
              <Title>Your payment method</Title>
              <PaymentMethodType
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                isElena
              />
            </>
          )}
          <PaymentAmountDistribution
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
          />
          <EmployeePaymentMethodDetails
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
            isPaymentsTab
            isElena
          />
        </Stack>
      </Stack>
    ),
  });
};

export const CommunicationMessage = ({
  sectionTitle,
  flags,
  employeeId,
  employerId,
  onboardingPeriodId,
  shouldShowZorroPayButton,
  shouldShowPaymentMethodType,
  onOpenSelfEnrollmentDrawer,
  isEmployeeMode,
}: CommunicationMessageProps) => {
  const [isModalOpen, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const { sendEvent } = useAnalytics();
  const [
    isZorroPayDrawerOpen,
    { open: openZorroPayDrawer, close: closeZorroPayDrawer },
  ] = useDisclosure(false);
  const [
    isAutoPayDrawerOpen,
    { open: openAutoPayDrawer, close: closeAutoPayDrawer },
  ] = useDisclosure(false);
  const [selectedAutoPayDate, setSelectedAutoPayDate] = useState<string | null>(
    null
  );
  const modalConfigRef = useRef<ModalConfig | null>(null);

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const renderIcon = (icon: CommunicationMessageConfig['flags'][0]['icon']) => {
    if (!icon) return null;
    return <Icon icon={icon.component} size={icon.size} style={icon.styles} />;
  };

  const renderButtonIcon = (icon?: ElementType) => {
    if (!icon) return null;
    const IconComponent = icon;
    return <IconComponent size={16} />;
  };

  const handleMarkAsDoneClick = async (
    onClick: (date?: string | null) => void | Promise<void>,
    flagType: FlagType
  ) => {
    if (AUTO_PAY_FLAG_TYPES.has(flagType)) {
      openAutoPayDrawer();
      await sendEvent('open_mark_as_done', {
        modal_title: AUTO_PAY_DRAWER_CONFIG.title,
        onboarding_period_id: onboardingPeriodId,
        flag_type: flagType,
      });
      return;
    }

    const employeeName = !isEmployeeMode
      ? (employee?.firstName ?? 'N/A')
      : undefined;
    const modalContent = getModalConfig(flagType, employeeName);

    if (!modalContent) {
      return;
    }

    modalConfigRef.current = {
      title: modalContent.title,
      message: modalContent.message,
      onConfirm: async () => {
        await onClick();
        closeModal();
      },
    };
    openModal();
    await sendEvent('open_mark_as_done', {
      modal_title: modalConfigRef.current?.title,
      onboarding_period_id: onboardingPeriodId,
      flag_type: flagType,
    });
  };

  const handleAutoPayConfirm = async () => {
    const autoPayFlag = flags.find((flag) =>
      AUTO_PAY_FLAG_TYPES.has(flag.type)
    );
    if (autoPayFlag && autoPayFlag.button) {
      await autoPayFlag.button.onClick(selectedAutoPayDate);
    }
    closeAutoPayDrawer();
    setSelectedAutoPayDate(null);
  };

  return (
    <Stack gap="md">
      {sectionTitle && (
        <Text size="md" fw={600}>
          {sectionTitle}
        </Text>
      )}
      <Stack gap="md">
        {flags.map((flag) => (
          <Card
            key={flag.title}
            bg={brand.zorroPlum200}
            w="100%"
            p="md"
            variant="white"
            style={{ borderRadius: '0.5rem' }}
          >
            <Group gap="xs" align="flex-start" wrap="nowrap">
              {renderIcon(flag.icon)}
              <Stack gap="md" style={{ flex: 1 }}>
                <Stack gap="xxs">
                  <Text size="sm" fw={600}>
                    {flag.title}
                  </Text>
                  {Array.isArray(flag.body) ? (
                    flag.body.length > 0 && (
                      <List size="sm" spacing="xxs">
                        {flag.body.map((item) => (
                          <List.Item key={item} style={{ paddingRight: '1em' }}>
                            {item}
                          </List.Item>
                        ))}
                      </List>
                    )
                  ) : (
                    <Text
                      size="sm"
                      component="span"
                      c="var(--color-icon-fill-system-secondary)"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {flag.body}
                      {flag.link && (
                        <>
                          {' '}
                          <Link
                            href={flag.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="secondary"
                          >
                            {flag.link.label}
                          </Link>
                        </>
                      )}
                    </Text>
                  )}
                  {flag.filesPageUrl && (
                    <FilesPageDocumentsNote filesPageUrl={flag.filesPageUrl} />
                  )}
                </Stack>

                {(flag.button || flag.secondaryButton) && (
                  <Group gap="xs">
                    {flag.button && (
                      <Button
                        onClick={async () => {
                          if (
                            flag.button?.label === BUTTON_LABELS.MARK_AS_DONE
                          ) {
                            await handleMarkAsDoneClick(
                              flag.button.onClick,
                              flag.type
                            );
                          } else if (flag.button) {
                            void flag.button.onClick();
                          }
                        }}
                        size="sm"
                        variant={mapLegacyVariant(flag.button.variant)}
                        w="fit-content"
                        leftSection={renderButtonIcon(flag.button.leftSection)}
                        rightSection={renderButtonIcon(
                          flag.button.rightSection
                        )}
                      >
                        {flag.button.label}
                      </Button>
                    )}
                    {shouldShowZorroPayButton && flag.secondaryButton && (
                      <Button
                        onClick={openZorroPayDrawer}
                        size="sm"
                        variant={mapLegacyVariant(flag.secondaryButton.variant)}
                        w="fit-content"
                        leftSection={renderButtonIcon(
                          flag.secondaryButton.leftSection
                        )}
                        rightSection={renderButtonIcon(
                          flag.secondaryButton.rightSection
                        )}
                      >
                        {flag.secondaryButton.label}
                      </Button>
                    )}
                  </Group>
                )}
                {flag.type === FlagType.SELF_ENROLL &&
                  onOpenSelfEnrollmentDrawer && (
                    <Button
                      onClick={onOpenSelfEnrollmentDrawer}
                      size="sm"
                      variant="primary"
                      w="fit-content"
                    >
                      Start your enrollment application
                    </Button>
                  )}
              </Stack>
            </Group>
          </Card>
        ))}
      </Stack>

      {modalConfigRef.current && (
        <ModalMessage
          isOpen={isModalOpen}
          onClose={async () => {
            closeModal();
            await sendEvent('modal_closed', {
              modal_title: modalConfigRef.current?.title,
              onboarding_period_id: onboardingPeriodId,
            });
          }}
          variant="regular"
          title={modalConfigRef.current.title}
          description={modalConfigRef.current.message}
          primaryLabel="Confirm"
          onPrimaryAction={modalConfigRef.current.onConfirm}
          secondaryLabel="Cancel"
          zIndex={MODAL_ZINDEX + 1}
          onSecondaryAction={async () => {
            closeModal();
            await sendEvent('modal_closed', {
              modal_title: modalConfigRef.current?.title,
              onboarding_period_id: onboardingPeriodId,
            });
          }}
        />
      )}

      <Drawer
        opened={isZorroPayDrawerOpen}
        onClose={closeZorroPayDrawer}
        position="right"
        size="lg"
        title={<Title>Zorro Pay</Title>}
      >
        <Stack gap="xl">
          <ZorroPayDrawerContent
            employeeId={employeeId}
            employerId={employerId}
            onboardingPeriodId={onboardingPeriodId}
            shouldShowPaymentMethodType={shouldShowPaymentMethodType}
          />
        </Stack>
      </Drawer>

      <Drawer
        opened={isAutoPayDrawerOpen}
        onClose={() => {
          closeAutoPayDrawer();
          setSelectedAutoPayDate(null);
        }}
        title={AUTO_PAY_DRAWER_CONFIG.title}
        position="right"
        footer={
          <FormFooter
            withPaper={false}
            primaryLabel="Confirm"
            primaryButtonProps={{
              onClick: handleAutoPayConfirm,
              disabled: !selectedAutoPayDate,
            }}
            secondaryLabel="Cancel"
            secondaryButtonProps={{
              onClick: () => {
                closeAutoPayDrawer();
                setSelectedAutoPayDate(null);
              },
            }}
          />
        }
      >
        <Stack gap="md">
          <Text size="sm">{AUTO_PAY_DRAWER_CONFIG.message}</Text>
          <Select
            label="Monthly auto-pay date"
            placeholder="Select monthly auto-pay date"
            data={MONTHLY_AUTO_PAY_DATE_DATA}
            value={selectedAutoPayDate}
            onChange={(val) => setSelectedAutoPayDate(val)}
          />
        </Stack>
      </Drawer>
    </Stack>
  );
};
